import { Editor, Node, mergeAttributes, Extension } from '@tiptap/core';
import { StarterKit } from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Underline } from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import { Typography } from '@tiptap/extension-typography';
import { CharacterCount } from '@tiptap/extension-character-count';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { HorizontalRule } from '@tiptap/extension-horizontal-rule';
import { Dropcursor } from '@tiptap/extension-dropcursor';
import { Gapcursor } from '@tiptap/extension-gapcursor';
import { common, createLowlight } from 'lowlight';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const lowlight = createLowlight(common);

/* ───────────────────────────────────────────
   Color Palette
   ─────────────────────────────────────────── */

export const TEXT_COLORS = [
    { name: '기본', color: null },
    { name: '회색', color: '#9ca3af' },
    { name: '갈색', color: '#92400e' },
    { name: '주황', color: '#ea580c' },
    { name: '노랑', color: '#ca8a04' },
    { name: '초록', color: '#16a34a' },
    { name: '파랑', color: '#2563eb' },
    { name: '보라', color: '#9333ea' },
    { name: '분홍', color: '#db2777' },
    { name: '빨강', color: '#dc2626' },
];

export const BG_COLORS = [
    { name: '기본', color: null },
    { name: '회색', color: '#f3f4f6' },
    { name: '갈색', color: '#fef3c7' },
    { name: '주황', color: '#ffedd5' },
    { name: '노랑', color: '#fef9c3' },
    { name: '초록', color: '#dcfce7' },
    { name: '파랑', color: '#dbeafe' },
    { name: '보라', color: '#f3e8ff' },
    { name: '분홍', color: '#fce7f3' },
    { name: '빨강', color: '#fee2e2' },
];

/* ───────────────────────────────────────────
   Custom Extensions
   ─────────────────────────────────────────── */

// Callout block extension
const Callout = Node.create({
    name: 'callout',
    group: 'block',
    content: 'block+',
    defining: true,
    addAttributes() {
        return {
            type: {
                default: 'info',
                parseHTML: (el: HTMLElement) => el.getAttribute('data-callout-type') || 'info',
                renderHTML: (attrs: any) => ({ 'data-callout-type': attrs.type }),
            },
        };
    },
    parseHTML() {
        return [{ tag: 'div[data-callout]' }];
    },
    renderHTML({ HTMLAttributes }: any) {
        const typeColors: any = {
            info: 'background:#eff6ff;border-left:4px solid #3b82f6;padding:12px 16px;border-radius:4px;margin:8px 0;',
            warning: 'background:#fffbeb;border-left:4px solid #f59e0b;padding:12px 16px;border-radius:4px;margin:8px 0;',
            error: 'background:#fef2f2;border-left:4px solid #ef4444;padding:12px 16px;border-radius:4px;margin:8px 0;',
            success: 'background:#f0fdf4;border-left:4px solid #22c55e;padding:12px 16px;border-radius:4px;margin:8px 0;',
        };
        const style = typeColors[HTMLAttributes['data-callout-type']] || typeColors.info;
        return ['div', mergeAttributes(HTMLAttributes, { 'data-callout': '', style }), 0];
    },
});

// Toggle (details/summary) extension
const ToggleList = Node.create({
    name: 'toggleList',
    group: 'block',
    content: 'block+',
    defining: true,
    addAttributes() {
        return {
            summary: {
                default: '토글',
                parseHTML: (el: HTMLElement) => el.querySelector('summary')?.textContent || '토글',
            },
        };
    },
    parseHTML() {
        return [{ tag: 'details' }];
    },
    renderHTML({ HTMLAttributes }: any) {
        return ['details', mergeAttributes(HTMLAttributes, { class: 'notion-toggle' }),
            ['summary', {}, HTMLAttributes.summary || '토글'],
            ['div', { class: 'notion-toggle-content' }, 0]
        ];
    },
});

// File attachment node
const FileAttachment = Node.create({
    name: 'fileAttachment',
    group: 'block',
    atom: true,
    addAttributes() {
        return {
            url: { default: null },
            name: { default: null },
            size: { default: null },
        };
    },
    parseHTML() {
        return [{ tag: 'div[data-file-attachment]' }];
    },
    renderHTML({ HTMLAttributes }: any) {
        return ['div', mergeAttributes(HTMLAttributes, {
            'data-file-attachment': '',
            class: 'notion-file-attachment',
            style: 'display:flex;align-items:center;gap:8px;padding:8px 12px;border:1px solid #e4e4e7;border-radius:8px;margin:4px 0;cursor:pointer;',
        }),
            ['span', { style: 'font-size:13px;color:#3f3f46;' }, HTMLAttributes.name || 'file'],
        ];
    },
});

// Column layout extension
const ColumnBlock = Node.create({
    name: 'columnBlock',
    group: 'block',
    content: 'column+',
    defining: true,
    isolating: true,
    addAttributes() {
        return {
            columns: {
                default: 2,
                parseHTML: (el: HTMLElement) => parseInt(el.getAttribute('data-columns') || '2'),
                renderHTML: (attrs: any) => ({ 'data-columns': attrs.columns }),
            },
        };
    },
    parseHTML() {
        return [{ tag: 'div[data-column-block]' }];
    },
    renderHTML({ HTMLAttributes }: any) {
        const cols = HTMLAttributes['data-columns'] || 2;
        return ['div', mergeAttributes(HTMLAttributes, {
            'data-column-block': '',
            style: `display:grid;grid-template-columns:repeat(${cols},1fr);gap:16px;margin:8px 0;`,
        }), 0];
    },
});

const Column = Node.create({
    name: 'column',
    group: '',
    content: 'block+',
    defining: true,
    isolating: true,
    parseHTML() {
        return [{ tag: 'div[data-column]' }];
    },
    renderHTML({ HTMLAttributes }: any) {
        return ['div', mergeAttributes(HTMLAttributes, {
            'data-column': '',
            style: 'min-width:0;',
        }), 0];
    },
});

// Clipboard image paste handler extension
const ClipboardImageHandler = Extension.create({
    name: 'clipboardImageHandler',
    addOptions() {
        return {
            uploadFn: null as ((file: File) => Promise<string | null>) | null,
        };
    },
    addProseMirrorPlugins() {
        const uploadFn = this.options.uploadFn;
        return [
            new Plugin({
                key: new PluginKey('clipboardImageHandler'),
                props: {
                    handlePaste: (view, event) => {
                        if (!uploadFn) return false;
                        const items = event.clipboardData?.items;
                        if (!items) return false;
                        for (let i = 0; i < items.length; i++) {
                            if (items[i].type.startsWith('image/')) {
                                event.preventDefault();
                                const file = items[i].getAsFile();
                                if (!file) return true;
                                uploadFn(file).then((url) => {
                                    if (url) {
                                        const { tr } = view.state;
                                        const node = view.state.schema.nodes['image'].create({ src: url });
                                        view.dispatch(tr.replaceSelectionWith(node));
                                    }
                                });
                                return true;
                            }
                        }
                        return false;
                    },
                    handleDrop: (view, event) => {
                        if (!uploadFn) return false;
                        const files = event.dataTransfer?.files;
                        if (!files || files.length === 0) return false;
                        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
                        if (imageFiles.length === 0) return false;
                        event.preventDefault();
                        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
                        for (const file of imageFiles) {
                            uploadFn(file).then((url) => {
                                if (url && pos) {
                                    const { tr } = view.state;
                                    const node = view.state.schema.nodes['image'].create({ src: url });
                                    view.dispatch(tr.insert(pos.pos, node));
                                }
                            });
                        }
                        return true;
                    },
                },
            }),
        ];
    },
});

// Tab/Shift+Tab indent extension + Notion keyboard shortcuts
const IndentHandler = Extension.create({
    name: 'indentHandler',
    addKeyboardShortcuts() {
        return {
            'Tab': ({ editor }) => {
                if (editor.isActive('listItem') || editor.isActive('taskItem')) {
                    return editor.chain().focus().sinkListItem(
                        editor.isActive('taskItem') ? 'taskItem' : 'listItem'
                    ).run();
                }
                return false;
            },
            'Shift-Tab': ({ editor }) => {
                if (editor.isActive('listItem') || editor.isActive('taskItem')) {
                    return editor.chain().focus().liftListItem(
                        editor.isActive('taskItem') ? 'taskItem' : 'listItem'
                    ).run();
                }
                return false;
            },
        };
    },
});

// Notion-style keyboard shortcuts (Ctrl/Cmd+Shift+1~8)
const NotionKeyboardShortcuts = Extension.create({
    name: 'notionKeyboardShortcuts',
    addKeyboardShortcuts() {
        return {
            // Block type shortcuts (Ctrl/Cmd+Shift+0~8)
            'Mod-Shift-0': ({ editor }) => editor.chain().focus().setParagraph().run(),
            'Mod-Shift-1': ({ editor }) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
            'Mod-Shift-2': ({ editor }) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
            'Mod-Shift-3': ({ editor }) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
            'Mod-Shift-4': ({ editor }) => editor.chain().focus().toggleTaskList().run(),
            'Mod-Shift-5': ({ editor }) => editor.chain().focus().toggleBulletList().run(),
            'Mod-Shift-6': ({ editor }) => editor.chain().focus().toggleOrderedList().run(),
            'Mod-Shift-7': ({ editor }) => editor.chain().focus().toggleBlockquote().run(),
            'Mod-Shift-8': ({ editor }) => editor.chain().focus().toggleCodeBlock().run(),

            // Formatting shortcuts
            'Mod-Shift-s': ({ editor }) => editor.chain().focus().toggleStrike().run(),

            // Duplicate block (Ctrl+D)
            'Mod-d': ({ editor }) => {
                const { $from, $to } = editor.state.selection;
                const blockStart = $from.start($from.depth);
                const blockEnd = $to.end($to.depth);
                const slice = editor.state.doc.slice(blockStart, blockEnd);
                editor.chain().focus().insertContentAt(blockEnd + 1, slice.content.toJSON()).run();
                return true;
            },

            // Horizontal rule (--- shortcut is handled by StarterKit, this is Ctrl+Shift+H)
            'Mod-Shift-h': ({ editor }) => editor.chain().focus().setHorizontalRule().run(),
        };
    },
});

/* ───────────────────────────────────────────
   Slash Command Menu Items
   ─────────────────────────────────────────── */

export interface SlashMenuItem {
    title: string;
    description: string;
    icon: string;
    category: string;
    keywords?: string[];
    command: (editor: Editor) => void;
}

export const SLASH_MENU_ITEMS: SlashMenuItem[] = [
    // 기본 블록
    { title: '텍스트', description: '일반 텍스트 블록', icon: 'Aa', category: '기본 블록',
      keywords: ['text', 'paragraph', '텍스트'],
      command: (e) => e.chain().focus().setParagraph().run() },
    { title: '제목 1', description: '대제목', icon: 'H1', category: '기본 블록',
      keywords: ['h1', 'heading', '제목', '대제목'],
      command: (e) => e.chain().focus().toggleHeading({ level: 1 }).run() },
    { title: '제목 2', description: '중제목', icon: 'H2', category: '기본 블록',
      keywords: ['h2', 'heading', '제목', '중제목'],
      command: (e) => e.chain().focus().toggleHeading({ level: 2 }).run() },
    { title: '제목 3', description: '소제목', icon: 'H3', category: '기본 블록',
      keywords: ['h3', 'heading', '제목', '소제목'],
      command: (e) => e.chain().focus().toggleHeading({ level: 3 }).run() },
    // 목록
    { title: '글머리 기호 목록', description: '간단한 목록', icon: '•', category: '목록',
      keywords: ['bullet', 'list', '목록', '리스트'],
      command: (e) => e.chain().focus().toggleBulletList().run() },
    { title: '번호 목록', description: '순서가 있는 목록', icon: '1.', category: '목록',
      keywords: ['number', 'ordered', '번호', '순서'],
      command: (e) => e.chain().focus().toggleOrderedList().run() },
    { title: '할 일 목록', description: '체크박스 목록', icon: '☑', category: '목록',
      keywords: ['todo', 'task', 'checkbox', '할일', '체크'],
      command: (e) => e.chain().focus().toggleTaskList().run() },
    // 고급 블록
    { title: '인용', description: '인용구 블록', icon: '❝', category: '고급 블록',
      keywords: ['quote', 'blockquote', '인용'],
      command: (e) => e.chain().focus().toggleBlockquote().run() },
    { title: '구분선', description: '가로 구분선', icon: '—', category: '고급 블록',
      keywords: ['divider', 'hr', 'horizontal', '구분선'],
      command: (e) => e.chain().focus().setHorizontalRule().run() },
    { title: '코드 블록', description: '코드 에디터', icon: '⟨⟩', category: '고급 블록',
      keywords: ['code', 'codeblock', '코드'],
      command: (e) => e.chain().focus().toggleCodeBlock().run() },
    { title: '콜아웃 (정보)', description: '정보 강조 영역', icon: '💡', category: '고급 블록',
      keywords: ['callout', 'info', '콜아웃', '정보'],
      command: (e) => e.chain().focus().insertContent({ type: 'callout', attrs: { type: 'info' }, content: [{ type: 'paragraph' }] }).run() },
    { title: '콜아웃 (경고)', description: '경고 강조 영역', icon: '⚠️', category: '고급 블록',
      keywords: ['callout', 'warning', '경고'],
      command: (e) => e.chain().focus().insertContent({ type: 'callout', attrs: { type: 'warning' }, content: [{ type: 'paragraph' }] }).run() },
    { title: '콜아웃 (에러)', description: '에러 강조 영역', icon: '❌', category: '고급 블록',
      keywords: ['callout', 'error', '에러'],
      command: (e) => e.chain().focus().insertContent({ type: 'callout', attrs: { type: 'error' }, content: [{ type: 'paragraph' }] }).run() },
    { title: '콜아웃 (성공)', description: '성공 강조 영역', icon: '✅', category: '고급 블록',
      keywords: ['callout', 'success', '성공'],
      command: (e) => e.chain().focus().insertContent({ type: 'callout', attrs: { type: 'success' }, content: [{ type: 'paragraph' }] }).run() },
    { title: '토글', description: '접기/펼치기 블록', icon: '▶', category: '고급 블록',
      keywords: ['toggle', '토글', '접기'],
      command: (e) => e.chain().focus().insertContent({ type: 'toggleList', attrs: { summary: '토글' }, content: [{ type: 'paragraph' }] }).run() },
    // 표
    { title: '표', description: '3×3 표 삽입', icon: '⊞', category: '고급 블록',
      keywords: ['table', '표', '테이블'],
      command: (e) => e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
    // 컬럼
    { title: '2단 컬럼', description: '2열 레이아웃', icon: '▥', category: '레이아웃',
      keywords: ['column', '컬럼', '열', '2단'],
      command: (e) => e.chain().focus().insertContent({
          type: 'columnBlock', attrs: { columns: 2 },
          content: [
              { type: 'column', content: [{ type: 'paragraph' }] },
              { type: 'column', content: [{ type: 'paragraph' }] },
          ]
      }).run() },
    { title: '3단 컬럼', description: '3열 레이아웃', icon: '▦', category: '레이아웃',
      keywords: ['column', '컬럼', '열', '3단'],
      command: (e) => e.chain().focus().insertContent({
          type: 'columnBlock', attrs: { columns: 3 },
          content: [
              { type: 'column', content: [{ type: 'paragraph' }] },
              { type: 'column', content: [{ type: 'paragraph' }] },
              { type: 'column', content: [{ type: 'paragraph' }] },
          ]
      }).run() },
    // 미디어
    { title: '이미지', description: '이미지 업로드', icon: '🖼', category: '미디어',
      keywords: ['image', '이미지', '사진'],
      command: (_e) => { /* handled by parent component */ } },
    { title: '파일', description: '파일 첨부', icon: '📎', category: '미디어',
      keywords: ['file', '파일', '첨부'],
      command: (_e) => { /* handled by parent component */ } },
];

/* ───────────────────────────────────────────
   "Turn into" block types for the block menu
   ─────────────────────────────────────────── */

export interface TurnIntoItem {
    title: string;
    icon: string;
    command: (editor: Editor) => void;
}

export const TURN_INTO_ITEMS: TurnIntoItem[] = [
    { title: '텍스트', icon: 'Aa', command: (e) => e.chain().focus().setParagraph().run() },
    { title: '제목 1', icon: 'H1', command: (e) => e.chain().focus().toggleHeading({ level: 1 }).run() },
    { title: '제목 2', icon: 'H2', command: (e) => e.chain().focus().toggleHeading({ level: 2 }).run() },
    { title: '제목 3', icon: 'H3', command: (e) => e.chain().focus().toggleHeading({ level: 3 }).run() },
    { title: '글머리 기호', icon: '•', command: (e) => e.chain().focus().toggleBulletList().run() },
    { title: '번호 목록', icon: '1.', command: (e) => e.chain().focus().toggleOrderedList().run() },
    { title: '할 일', icon: '☑', command: (e) => e.chain().focus().toggleTaskList().run() },
    { title: '인용', icon: '❝', command: (e) => e.chain().focus().toggleBlockquote().run() },
    { title: '코드', icon: '⟨⟩', command: (e) => e.chain().focus().toggleCodeBlock().run() },
];

/* ───────────────────────────────────────────
   Editor Factory
   ─────────────────────────────────────────── */

export interface NotionEditorOptions {
    element: HTMLElement;
    content?: any;
    editable?: boolean;
    placeholder?: string;
    onUpdate?: (json: any) => void;
    onCreate?: (editor: Editor) => void;
    uploadImageFn?: (file: File) => Promise<string | null>;
}

export function createNotionEditor(options: NotionEditorOptions): Editor {
    const {
        element,
        content = null,
        editable = true,
        placeholder = "내용을 입력하세요. '/'를 입력하면 명령어를 사용할 수 있습니다.",
        onUpdate,
        onCreate,
        uploadImageFn,
    } = options;

    const extensions: any[] = [
        StarterKit.configure({
            heading: { levels: [1, 2, 3] },
            codeBlock: false,
            horizontalRule: false,
            dropcursor: false,
            gapcursor: false,
        }),
        Placeholder.configure({
            placeholder: ({ node }) => {
                if (node.type.name === 'heading') {
                    const level = node.attrs.level;
                    return `제목 ${level}`;
                }
                return placeholder;
            },
        }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Table.configure({ resizable: true }),
        TableRow,
        TableCell,
        TableHeader,
        Image.configure({
            allowBase64: false,
            HTMLAttributes: { class: 'notion-image' },
        }),
        Link.configure({
            openOnClick: false,
            autolink: true,
            HTMLAttributes: { class: 'notion-link' },
        }),
        Underline,
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Typography,
        CharacterCount,
        CodeBlockLowlight.configure({ lowlight }),
        Subscript,
        Superscript,
        HorizontalRule,
        Dropcursor.configure({ color: '#3b82f6', width: 2 }),
        Gapcursor,
        Callout,
        ToggleList,
        FileAttachment,
        ColumnBlock,
        Column,
        IndentHandler,
        NotionKeyboardShortcuts,
    ];

    if (uploadImageFn) {
        extensions.push(ClipboardImageHandler.configure({ uploadFn: uploadImageFn }));
    }

    const editor = new Editor({
        element,
        editable,
        content,
        extensions,
        editorProps: {
            attributes: {
                class: 'tiptap',
                spellcheck: 'false',
            },
        },
        onUpdate: ({ editor: e }) => {
            if (onUpdate) onUpdate(e.getJSON());
        },
        onCreate: ({ editor: e }) => {
            if (onCreate) onCreate(e);
        },
    });

    return editor;
}

/* ─── Exports ─── */
export {
    Editor,
    Node,
    Extension,
    mergeAttributes,
    Callout,
    ToggleList,
    FileAttachment,
    ColumnBlock,
    Column,
    ClipboardImageHandler,
    IndentHandler,
    NotionKeyboardShortcuts,
    SLASH_MENU_ITEMS as SlashMenuItems,
};
